import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import IssueOptionsDialog from "./IssueOptionsDialog";
import SampleSelectionChecklist from "./SampleSelectionChecklist";

const RadioSelect = ({ value, text, onChange, onNoSelect }) => {
  const handleChange = useCallback(
    (e) => {
      const isYes = e.target.value === "yes";
      onChange(isYes);

      if (!isYes && onNoSelect) {
        onNoSelect();
      }
    },
    [onChange, onNoSelect]
  );

  return (
    <>
      <div className="columns">
        <div className="column is-four-fifths">
          <label htmlFor="" className="label">
            {text}
          </label>
        </div>
        <div className="column">
          <div className="control">
            <label className="radio">
              <input
                type="radio"
                checked={value === true}
                onChange={handleChange}
                value="yes"
              />
              Yes
            </label>
            <label className="radio">
              <input
                type="radio"
                checked={value === false}
                onChange={handleChange}
                value="no"
              />
              No
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default function SampleScannedForm(props) {
  const { register, handleSubmit, control, setValue, watch } = useForm({
    defaultValues: props.model,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [activeField, setActiveField] = useState("");

  const questionsConfig = {
    cocCompleted: {
      text: "Chain of Custody is present & filled out completely in ink and legible?",
      fieldName: "cocCompletedComments",
      options: [
        { id: "1", text: "Client did not relinquish the COC" },
        {
          id: "2",
          text: "Client did not provide collection information on the COC, we took the information from the sample bottles and applied it to the COC.",
        },
        {
          id: "3",
          text: "Client did not provide collection information on the COC, we contacted the client who provided us with the information and permission to record it on the COC.",
        },
        { id: "Other", text: "Other" },
      ],
    },

    correctLabels: {
      text: "Sample Labels match COC? (Sample IDs, Test Names, Collection Dates & Times)",
      fieldName: "correctLabelsComments",
      options: [
        {
          id: "4",
          text: "Collection information not recorded on the sample bottles. We took the information from the COC and recorded it on the sample bottles",
        },
        {
          id: "5",
          text: "Collection information on the sample bottles did not match the information on the COC. We corrected the information on the sample bottles to match the COC",
        },
        { id: "Other", text: "Other" },
      ],
    },
    isRush: {
      text: "Rush TAT requested (5 days or less)?",
      fieldName: "rushTatComments",
      options: [
        { id: "1day", text: "1 day Rush" },
        { id: "3day", text: "3 days Rush" },
        { id: "5day", text: "5 days Rush" },
        { id: "Other", text: "Other" },
      ],
    },
  };

  const shouldShowPrePostPayQuestions =
    props.model.orderPayType === null || props.model.orderPayType === "";

  // Require at least one sample selected when there are samples to pick from.
  // Orders without sample data available fall back to the old unrestricted behavior.
  // Updating a submitted sheet has no selection to make, so the rule doesn't apply.
  const hasSamplesToPick = !props.isEditing && (props.unsubmittedSamples || []).length > 0;
  const nothingSelected = hasSamplesToPick && (props.selectedSampleIds || []).length === 0;

  useEffect(() => {
    setValue("headSpacePresent", props.model.headSpacePresent);
  }, [props.model.headSpacePresent, setValue]);
  

  const onResetHeadSpace = () => {
    setValue("headSpacePresent", null);
    props.onResetHeadSpace();
  };

  const handleSaveAnswer = (answer) => {
    if (activeField && currentQuestion) {
      const fieldName = questionsConfig[activeField].fieldName;
      setValue(fieldName, answer);
    }
    setDialogOpen(false);
  };

  return (
    <>
      {!props.isLoading && (
        <form onSubmit={handleSubmit(props.onSubmit)}>
          <div className="columns">
            <div className="column">
              {props.webUrl !== null && props.webUrl !== "" && (
                <a
                  className="button is-link is-hovered is-small"
                  href={props.webUrl}
                  target="_blank"
                >
                  {" "}
                  Open Form
                </a>
              )}

              {props.model.willRushPay !== null &&
                props.model.willRushPay === true && (
                  <label
                    className="label tag is-primary"
                    style={{ marginLeft: "10px" }}
                  >
                    Need Rush
                  </label>
                )}
              {!shouldShowPrePostPayQuestions && (
                <label
                  className="label tag is-primary"
                  style={{ marginLeft: "10px" }}
                >
                  Kit Paid
                </label>
              )}
            </div>
          </div>

          <SampleSelectionChecklist
            samples={props.isEditing ? props.sheetSamples : props.unsubmittedSamples}
            selectedIds={props.selectedSampleIds || []}
            onChange={props.onSelectedSampleIdsChange}
            checkInSheetCount={props.checkInSheetCount}
            totalSampleCount={props.totalSampleCount}
            remainingSampleCount={props.remainingSampleCount}
            readOnly={props.isEditing}
            sheetNumber={props.sheetNumber}
          />

          <div className="columns">
            <div className="column">
              <label className="label" htmlFor="">
                Received Date
              </label>
              <input
                className="input"
                type="datetime-local"
                {...register("dateReceied")}
              />
            </div>
            <div className="column">
              <label className="label" htmlFor="">
                Delivery Method
              </label>
              <select className="input" {...register("deliveryMethod")}>
                <option value="PickUp">ELI Route</option>
                <option value="ClientWalkIn">Walk-in</option>
                <option value="FedEx">FedEx</option>
                <option value="USPS">USPS</option>
                <option value="UPS">UPS</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          {shouldShowPrePostPayQuestions && (
            <div className="columns">
              <div className="column is-three-quarters">
                <label htmlFor="" className="label">
                  Cooler temperature upon lab receipt (℃)
                </label>
              </div>
              <div className="column">
                <input
                  className="input is-small"
                  {...register("coolerTemperature")}
                  type="number"
                  step=".01"
                />
              </div>
            </div>
          )}
          {shouldShowPrePostPayQuestions && (
            <div className="columns">
              <div className="column is-three-quarters">
                <label htmlFor="" className="label">
                  Sample received on
                </label>
              </div>
              <div className="column">
                <select className="input is-small" {...register("receivedOn")}>
                  <option value="Wet Ice">Wet Ice</option>
                  <option value="Ice Packs">Ice Packs</option>
                  <option value="None">None</option>
                </select>
              </div>
            </div>
          )}
          <div className="box">
            {shouldShowPrePostPayQuestions && (
              <Controller
                name="coolerTempAcceptable"
                render={({ field: { value, onChange } }) => (
                  <RadioSelect
                    value={value}
                    text="Cooler temperature is acceptable? (0-6 ℃ if required)"
                    onChange={onChange}
                  />
                )}
                control={control}
              />
            )}

            <Controller
              name="cocCompleted"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div>
                  <RadioSelect
                    value={value}
                    text="Chain of Custody is present & filled out completely in ink and legible?"
                    onChange={(val) => {
                      onChange(val);
                      if (val === false) {
                        setActiveField("cocCompleted");
                        setCurrentQuestion(questionsConfig.cocCompleted);
                        setDialogOpen(true);
                      } else {
                        setValue("emptyComment", "");
                      }
                    }}
                  />
                  {/* Show comment if exists */}
                  {watch("cocCompletedComments") && (
                    <div
                      style={{
                        marginTop: "0.5rem",
                        padding: "0.5rem",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                      }}
                    >
                      <strong>Comment:</strong> {watch("cocCompletedComments")}
                    </div>
                  )}
                </div>
              )}
            />

            {shouldShowPrePostPayQuestions && (
              <Controller
                name="correctLabels"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div>
                    <RadioSelect
                      value={value}
                      text="Sample Labels match COC? (Sample IDs, Test Names, Collection Dates & Times)"
                      onChange={(val) => {
                        onChange(val);
                        if (val === false) {
                          setActiveField("correctLabels");
                          setCurrentQuestion(questionsConfig.correctLabels);
                          setDialogOpen(true);
                        }
                      }}
                    />
                    {watch("correctLabelsComments") && (
                      <div
                        style={
                          {
                            /* same styling as above */
                          }
                        }
                      >
                        <strong>Comment:</strong>{" "}
                        {watch("correctLabelsComments")}
                      </div>
                    )}
                  </div>
                )}
              />
            )}

            <Controller
              name="containerIntact"
              render={({ field: { value, onChange } }) => (
                <RadioSelect
                  value={value}
                  text="Sample Containers Intact (no breakage or leaking)?"
                  onChange={onChange}
                />
              )}
              control={control}
            />
            <Controller
              name="sufficientVolume"
              render={({ field: { value, onChange } }) => (
                <RadioSelect
                  value={value}
                  text="Sample Volume Sufficient for all requested analysis including any requested for QC purpose (such as MS/MSD)?"
                  onChange={onChange}
                />
              )}
              control={control}
            />
            <Controller
              name="withinHoldingTime"
              render={({ field: { value, onChange } }) => (
                <RadioSelect
                  value={value}
                  text="Sample Received within holding time?"
                  onChange={onChange}
                />
              )}
              control={control}
            />
          </div>
          <div className="box">
            <Controller
              name="shortHoldingTime"
              render={({ field: { value, onChange } }) => (
                <RadioSelect
                  value={value}
                  text="Short holding time analysis (48hrs or less)"
                  onChange={onChange}
                />
              )}
              control={control}
            />
            <Controller
              name="isRush"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div>
                  <RadioSelect
                    value={value}
                    text="Rush TAT requested (5 days or less)?"
                    onChange={(val) => {
                      onChange(val);
                      if (val === true) {
                        setActiveField("isRush");
                        setCurrentQuestion(questionsConfig.isRush);
                        setDialogOpen(true);
                      }
                    }}
                  />
                  {watch("rushTatComments") && (
                    <div
                      style={
                        {
                          /* same styling as above */
                        }
                      }
                    >
                      <strong>Comment:</strong> {watch("rushTatComments")}
                    </div>
                  )}
                </div>
              )}
            />

            {shouldShowPrePostPayQuestions && (
              <Controller
                name="noSplit"
                render={({ field: { value, onChange } }) => (
                  <RadioSelect
                    value={value}
                    text="Did client request samples to be split or composited?"
                    onChange={onChange}
                  />
                )}
                control={control}
              />
            )}
          </div>
          <div className="box" style={{ position: "relative" }}>
            <Controller
              name="headSpacePresent"
              render={({ field: { value, onChange } }) => (
                <RadioSelect
                  value={value}
                  text="Head Space present in vials (air bubble size>6mm)? (only applied to VOC or TTHM samples)"
                  onChange={onChange}
                />
              )}
              control={control}
            />
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                marginRight: "40px",
                marginBottom: "5px",
              }}
            >
              <button
                type="button"
                className="button is-primary is-outlined"
                onClick={onResetHeadSpace}
              >
                Reset
              </button>
            </div>
          </div>
          <div className="columns">
            <div className="column">
              <table className="table is-striped is-fullwidth is-narrow">
                <thead>
                  <tr>
                    <td>Sample#</td>
                    <td>pH</td>
                    <td>Sample#</td>
                    <td>pH</td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Sample 1</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph1")}
                      />
                    </td>
                    <td>Sample 7</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph7")}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Sample 2</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph2")}
                      />
                    </td>
                    <td>Sample 8</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph8")}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Sample 3</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph3")}
                      />
                    </td>
                    <td>Sample 9</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph9")}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Sample 4</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph4")}
                      />
                    </td>
                    <td>Sample 10</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph10")}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Sample 5</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph5")}
                      />
                    </td>
                    <td>Sample 11</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph11")}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Sample 6</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph6")}
                      />
                    </td>
                    <td>Sample 12</td>
                    <td>
                      <input
                        type="text"
                        className="input is-small"
                        {...register("ph12")}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="columns">
            <div className="column">
              <textarea
                className="textarea"
                rows="5"
                {...register("labComments")}
                placeholder="Lab Comments"
              ></textarea>
            </div>
          </div>
          <div className="columns align-right has-text-right">
            <div className="column">
              <input
                type="submit"
                value={
                  props.isEditing
                    ? `Update Check-In Sheet #${props.sheetNumber}`
                    : "Submit"
                }
                disabled={nothingSelected}
                title={nothingSelected ? "Select at least one sample to submit" : undefined}
                className={`button is-success ${
                  props.isLoading ? " is-loading" : ""
                } `}
              />
            </div>
          </div>

          <input type="hidden" {...register("cocCompletedComments")} />
          <input type="hidden" {...register("correctLabelsComments")} />
          <input type="hidden" {...register("rushTatComments")} />
        </form>
      )}
      {props.isLoading && (
        <progress className="progress is-large is-info" max="100">
          60%
        </progress>
      )}

      <IssueOptionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        question={currentQuestion?.text}
        options={currentQuestion?.options || []}
        onSave={handleSaveAnswer}
      />
    </>
  );
}
